/**
 * #724 — whose S opt-in is it?
 *
 * Display settings live in browser storage and are shared by every trader who
 * signs in on that browser. A plain boolean therefore let the next trader on a
 * shared desk inherit a key that fires two orders, and switch it for the
 * previous one. The opt-in now records the trader who turned it on, and counts
 * only for that trader; for anyone else S is off, exactly as if never enabled.
 *
 * A stored `true` with no owner (written before the owner existed) reads as
 * off: the trader opts in again once, deliberately.
 */
export interface TwoSidedOptInState {
  lt2TwoSidedEnabled: boolean;
  lt2TwoSidedOwnerId: string | null;
}

export function twoSidedOptedIn(s: TwoSidedOptInState, viewerId: string | null): boolean {
  return s.lt2TwoSidedEnabled && viewerId != null && s.lt2TwoSidedOwnerId === viewerId;
}

/**
 * #1067 — the Shift+S multiplier as this trader may use it, or why they can't.
 *
 * The listener, the header readout and Settings all ask this one question, so
 * none of them can call Shift+S available while another says it is not. Bound
 * to the trader exactly like the S opt-in: a multiplier another trader set on
 * this browser reads as "not set" for you. Shift+S is the S press made larger,
 * so it exists only while S itself is on for you.
 *
 * The reason is trader-facing and doubles as the instruction to fix it.
 */
export interface TwoSidedMultiplierState extends TwoSidedOptInState {
  lt2TwoSidedMultiplier: number | null;
  lt2TwoSidedMultiplierOwnerId: string | null;
}

export type TwoSidedMultiplierResolution =
  | { ok: true; multiplier: number }
  | { ok: false; reason: string };

export function twoSidedMultiplier(
  s: TwoSidedMultiplierState,
  viewerId: string | null,
): TwoSidedMultiplierResolution {
  if (!twoSidedOptedIn(s, viewerId)) {
    return { ok: false, reason: 'turn on Post both sides in Settings first' };
  }
  const n = s.lt2TwoSidedMultiplier;
  if (
    n == null ||
    s.lt2TwoSidedMultiplierOwnerId !== viewerId ||
    !Number.isSafeInteger(n) ||
    n < 2
  ) {
    return { ok: false, reason: 'no size multiplier set — enter a whole number 2 or more in Settings' };
  }
  return { ok: true, multiplier: n };
}
